// File Location: src/components/common/SignOutButton.jsx

import React, { useState } from 'react';
import { createPortal } from 'react-dom';
// import { useNavigate } from 'react-router-dom';
import {
  LogOut,
  Loader,
  AlertTriangle,
  X,
  User,
  Banknote,
  FileText,
  ShieldCheck
} from 'lucide-react';
import { signOut } from 'firebase/auth';
import { auth } from '../../services/firebaseConfig';
import { useAuth } from '../../context/AuthContext';
// import { cacheService } from '../../services/cacheService';

const SignOutButton = ({ 
  variant = 'button', // 'button', 'dropdown', 'icon', 'text'
  size = 'md', // 'sm', 'md', 'lg'
  showConfirmModal = false,
  confirmMode = 'modal', // 'modal' | 'dropdown'
  className = '',
  children = null
}) => {
  // const navigate = useNavigate();
  const { userData, roles, activeRole, logout } = useAuth();
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [showModal, setShowModal] = useState(false);

  // Determine active role in a normalized way (covers 'FinancialTeam' and 'PolicyWriter')
  const rawRole = (activeRole || userData?.role || '').toString();
  const normalizedRole = rawRole.replace(/\s+/g, '').trim().toLowerCase();
  const normalizedRoles = Array.isArray(roles)
    ? roles.map(r => (r || '').toString().replace(/\s+/g, '').trim().toLowerCase())
    : [];
  const roleTokens = new Set([
    normalizedRole,
    ...normalizedRoles
  ]);
  const containsAny = (value, needles) => needles.some(n => value.includes(n));
  const matchesAnyToken = (tokens, needles) =>
    Array.from(tokens).some(t => containsAny(t, needles));
  const isFinance = matchesAnyToken(roleTokens, ['financialteam', 'financial', 'finance']);
  const isPolicy = matchesAnyToken(roleTokens, ['policywriter', 'policy_team', 'policy']);

  // Role-aware creative content
  const getRoleConfirmConfig = () => {
    if (isFinance) {
      return {
        title: 'Balance the books before you bounce?',
        subtitle: "You're about to sign out from Finance.",
        highlight: 'Any unsaved numbers will vanish like petty cash. ',
        icon: <Banknote className="h-5 w-5 text-emerald-600" />,
        accentBg: 'bg-emerald-100',
        accentText: 'text-emerald-700',
      };
    }
    if (isPolicy) {
      return {
        title: 'Close this policy chapter now?',
        subtitle: "You're about to sign out from Policy.",
        highlight: 'Unsaved clauses will be redrafted by the void. ',
        icon: <FileText className="h-5 w-5 text-indigo-600" />,
        accentBg: 'bg-indigo-100',
        accentText: 'text-indigo-700',
      };
    }
    return {
      title: 'Confirm Sign Out',
      subtitle: 'You will be logged out of your account',
      highlight: '',
      icon: <AlertTriangle className="h-5 w-5 text-red-600" />,
      accentBg: 'bg-red-100',
      accentText: 'text-red-700',
    };
  };

  const roleConfig = getRoleConfirmConfig();

  // Handle user signout
  const handleSignOut = async () => {
    setIsSigningOut(true);
    
    try {
      console.log('Starting sign out process...');
      
      // Call the logout function from auth context - this handles all cache clearing and Firebase sign out
      if (logout) {
        await logout();
        console.log('Complete logout successful');
        // The logout function in AuthContext will handle the redirect to /login
      } else {
        // Fallback if logout function is not available
        await signOut(auth);
        window.location.href = '/login';
      }
      
    } catch (error) {
      console.error('Error signing out:', error);
      alert('Error signing out. Please try again.');
      // Even if error occurs, redirect to login
      window.location.href = '/login';
    } finally {
      setIsSigningOut(false);
      setShowModal(false);
    }
  };

  // Handle click - show modal if required, otherwise sign out directly
  const handleClick = () => {
    // Force confirmation for Finance/Policy; otherwise respect prop
    if (isFinance || isPolicy || showConfirmModal) {
      setShowModal(true);
    } else {
      handleSignOut();
    }
  };

  // Get size classes
  const getSizeClasses = () => {
    switch (size) {
      case 'sm':
        return 'px-2 py-1 text-sm';
      case 'lg':
        return 'px-6 py-3 text-lg';
      default:
        return 'px-4 py-2 text-base';
    }
  };

  // Get icon size
  const getIconSize = () => {
    switch (size) {
      case 'sm':
        return 'h-3 w-3';
      case 'lg':
        return 'h-6 w-6';
      default:
        return 'h-4 w-4';
    }
  };

  // Render different variants
  const renderButton = () => {
    const baseClasses = `inline-flex items-center justify-center font-medium rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed ${getSizeClasses()} ${className}`;
    const iconSizeClass = getIconSize();

    switch (variant) {
      case 'icon':
        return (
          <button
            onClick={handleClick}
            disabled={isSigningOut}
            className={`${baseClasses} bg-red-50 text-red-600 hover:bg-red-100 border border-red-200`}
            title="Sign Out"
          >
            {isSigningOut ? (
              <Loader className={`${iconSizeClass} animate-spin`} />
            ) : (
              <LogOut className={iconSizeClass} />
            )}
          </button>
        );

      case 'text':
        return (
          <button
            onClick={handleClick}
            disabled={isSigningOut}
            className={`${baseClasses} text-red-600 hover:text-red-800 hover:bg-red-50`}
          >
            {isSigningOut ? (
              <>
                <Loader className={`${iconSizeClass} mr-2 animate-spin`} />
                Signing Out...
              </>
            ) : (
              <>
                <LogOut className={`${iconSizeClass} mr-2`} />
                Sign Out
              </>
            )}
          </button>
        );

      case 'dropdown':
        return (
          <button
            onClick={handleClick}
            disabled={isSigningOut}
            className={`${baseClasses} w-full text-left text-red-600 hover:bg-red-50 hover:text-red-800`}
          >
            {isSigningOut ? (
              <>
                <Loader className={`${iconSizeClass} mr-3 animate-spin`} />
                Signing Out...
              </>
            ) : (
              <>
                <LogOut className={`${iconSizeClass} mr-3`} />
                Sign Out
              </>
            )}
          </button>
        );

      default: // 'button'
        return (
          <button
            onClick={handleClick}
            disabled={isSigningOut}
            className={`${baseClasses} bg-red-600 text-white hover:bg-red-700 focus:ring-2 focus:ring-red-500 focus:ring-offset-2 shadow-sm`}
          >
            {isSigningOut ? (
              <>
                <Loader className={`${iconSizeClass} mr-2 animate-spin`} />
                Signing Out...
              </>
            ) : (
              <>
                <LogOut className={`${iconSizeClass} mr-2`} />
                {children || 'Sign Out'}
              </>
            )}
          </button>
        );
    }
  };

  return (
    <div className="relative inline-block">
      {renderButton()}

      {/* Confirmation UI */}
      {showModal && confirmMode === 'dropdown' && (
        <div className="absolute right-0 mt-2 z-[100] w-[min(90vw,28rem)] bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden">
          <div className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">{roleConfig.title}</h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="mb-4">
              <div className="flex items-center space-x-3 mb-4">
                <div className={`${roleConfig.accentBg} p-2 rounded-full`}>
                  {roleConfig.icon}
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">{roleConfig.subtitle}</h4>
                  <p className="text-sm text-gray-600">{roleConfig.highlight}You will be logged out of your account.</p>
                </div>
              </div>

              {userData && (
                <div className="bg-gray-50 rounded-lg p-3 border">
                  <div className="flex items-center space-x-3">
                    <div className="bg-gray-200 p-2 rounded-full">
                      <User className="h-4 w-4 text-gray-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {userData.profile?.name || userData.entityName || userData.hospitalName || userData.email}
                      </p>
                      <div className="flex items-center space-x-2 text-xs text-gray-500">
                        <ShieldCheck className="h-3 w-3" />
                        <span>{activeRole || userData.role}</span>
                        {Array.isArray(roles) && roles.length > 1 && (
                          <span className="ml-2 text-gray-400">({roles.join(', ')})</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <p className="text-sm text-gray-600 mt-4">
                Are you sure you want to sign out? You'll need to log in again to access your account.
              </p>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowModal(false)}
                disabled={isSigningOut}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSignOut}
                disabled={isSigningOut}
                className={`inline-flex items-center px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${isFinance ? 'bg-emerald-600 hover:bg-emerald-700' : isPolicy ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-red-600 hover:bg-red-700'}`}
              >
                {isSigningOut ? (
                  <>
                    <Loader className="h-4 w-4 mr-2 animate-spin" />
                    Signing Out...
                  </>
                ) : (
                  <>
                    <LogOut className="h-4 w-4 mr-2" />
                    Sign Out
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {showModal && confirmMode === 'modal' && createPortal(
        (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-[1000] grid place-items-center p-2 sm:p-4">
            <div className="bg-white rounded-2xl w-[min(100vw-1rem,32rem)] max-h-[85vh] shadow-xl overflow-hidden">
              <div className="p-6 overflow-y-auto">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">{roleConfig.title}</h3>
                  <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                    <X className="h-6 w-6" />
                  </button>
                </div>
                <div className="mb-4">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className={`${roleConfig.accentBg} p-2 rounded-full`}>
                      {roleConfig.icon}
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900">{roleConfig.subtitle}</h4>
                      <p className="text-sm text-gray-600">{roleConfig.highlight}You will be logged out of your account</p>
                    </div>
                  </div>
                  {userData && (
                    <div className="bg-gray-50 rounded-lg p-3 border">
                      <div className="flex items-center space-x-3">
                        <div className="bg-gray-200 p-2 rounded-full">
                          <User className="h-4 w-4 text-gray-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{userData.profile?.name || userData.entityName || userData.hospitalName || userData.email}</p>
                          <div className="flex items-center space-x-2 text-xs text-gray-500">
                            <ShieldCheck className="h-3 w-3" />
                            <span>{activeRole || userData.role}</span>
                            {Array.isArray(roles) && roles.length > 1 && (
                              <span className="ml-2 text-gray-400">({roles.join(', ')})</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  <p className="text-sm text-gray-600 mt-4">Are you sure you want to sign out? You'll need to log in again to access your account.</p>
                </div>
                <div className="flex justify-end space-x-3">
                  <button onClick={() => setShowModal(false)} disabled={isSigningOut} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50">Cancel</button>
                  <button onClick={handleSignOut} disabled={isSigningOut} className={`inline-flex items-center px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${isFinance ? 'bg-emerald-600 hover:bg-emerald-700' : isPolicy ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-red-600 hover:bg-red-700'}`}>
                    {isSigningOut ? (<><Loader className="h-4 w-4 mr-2 animate-spin" />Signing Out...</>) : (<><LogOut className="h-4 w-4 mr-2" />Sign Out</>)}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ),
        document.body
      )}
    </div>
  );
};

// Pre-configured variants for common use cases
export const SignOutIconButton = (props) => (
  <SignOutButton variant="icon" {...props} />
);

export const SignOutTextButton = (props) => (
  <SignOutButton variant="text" {...props} />
);

export const SignOutDropdownItem = (props) => (
  <SignOutButton variant="dropdown" {...props} />
);

export const SignOutButtonWithConfirm = (props) => (
  <SignOutButton showConfirmModal={true} {...props} />
);

export default SignOutButton;